import { useEffect, useState } from "react";

import { toast } from "sonner";
import { ExternalMigrationCard } from "../components/ExternalMigrationCard";
import { PhotoBackfillCard } from "../components/PhotoBackfillCard";
import { StorageUsageCard } from "../components/StorageUsageCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A registry row with nothing worth keeping — no users, no jobs, no trucks.
// The backend decides this (`is_empty`) and the purge endpoint uses the same
// check, so the button shown always matches what the server will accept.
// Guessing it here from the counts is what made a row unremovable: Delete was
// refused server-side while Archive was hidden client-side.
const isEmptyShell = (t) => t.is_empty === true;

export default function PlatformAdminPage() {

  const [token, setToken] = useState(
    localStorage.getItem("platform_token") || ""
  );
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(null); // slug being reset
  const [newPw, setNewPw] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [exporting, setExporting] = useState(null);
  const [danger, setDanger] = useState(null); // { slug, action: archive|purge }
  const [confirmText, setConfirmText] = useState("");
  const [dangerBusy, setDangerBusy] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${token}` });

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${API}/platform/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      const b = await r.json();
      if (!r.ok) {
        toast.error(b.detail || "Login failed");
        return;
      }
      localStorage.setItem("platform_token", b.access_token);
      setToken(b.access_token);
      toast.success(`Welcome, ${b.user.name}`);
    } catch {
      toast.error("Network error");
    }
  };

  const loadTenants = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/platform/tenants`, { headers: auth() });
      if (r.status === 401 || r.status === 403) {
        localStorage.removeItem("platform_token");
        setToken("");
        return;
      }
      setTenants(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadTenants();
  }, [token]);

  const impersonate = async (slug) => {
    const r = await fetch(`${API}/platform/impersonate/${slug}`, {
      method: "POST",
      headers: auth(),
    });
    const b = await r.json();
    if (!r.ok) {
      toast.error(b.detail || "Impersonation failed");
      return;
    }
    // Swap the tenant token into normal auth storage — user is now
    // "logged in" as that tenant's admin.
    //
    // These MUST be the keys `lib/auth.jsx` reads (`rescue_token` /
    // `rescue_user`). Writing plain "token"/"user" is what sent platform
    // admins straight back to the login screen (Jun 2026). We also hard
    // navigate rather than react-route, so AuthProvider re-boots from
    // storage and the sidebar picks up that tenant's branding instead of
    // whatever was cached.
    localStorage.setItem("rescue_token", b.access_token);
    localStorage.removeItem("rescue_user");   // /auth/me fills in the real one
    localStorage.removeItem("tenant_tz");
    localStorage.removeItem("tenant_locale");
    if (typeof caches !== "undefined") caches.delete("tow-api-v1").catch(() => {});
    toast.success(`Impersonating ${slug} — session expires in 2h`);
    window.location.href = "/";
  };

  const toggleActive = async (slug, current) => {
    // Suspending cuts a paying customer off mid-shift, so it asks first.
    // Reactivating is harmless and goes straight through.
    if (current) {
      setDanger({ slug, action: "suspend" });
      return;
    }
    const r = await fetch(`${API}/platform/tenants/${slug}/active`, {
      method: "PATCH",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ active: true }),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => ({}));
      toast.error(b.detail || "Toggle failed");
      return;
    }
    toast.success(`${slug} reactivated`);
    loadTenants();
  };

  const setProtection = async (slug, protectedOn) => {
    const r = await fetch(`${API}/platform/tenants/${slug}/protected`, {
      method: "PATCH",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ protected: protectedOn }),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => ({}));
      toast.error(b.detail || "Could not change protection");
      return;
    }
    toast.success(
      protectedOn
        ? `${slug} protected`
        : `${slug} unprotected — it can now be suspended or archived`
    );
    loadTenants();
  };

  const exportTenant = async (slug) => {
    setExporting(slug);
    try {
      const r = await fetch(`${API}/platform/tenants/${slug}/export`, {
        headers: auth(),
      });
      if (!r.ok) {
        toast.error("Export failed");
        return;
      }
      const blob = await r.blob();
      const name =
        (r.headers.get("content-disposition") || "").match(/filename="(.+?)"/)?.[1] ||
        `towhook-export-${slug}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${slug}`);
    } catch {
      toast.error("Network error");
    } finally {
      setExporting(null);
    }
  };

  const restoreTenant = async (slug) => {
    toast.info(`Restoring ${slug}…`);
    const r = await fetch(`${API}/platform/tenants/${slug}/restore`, {
      method: "POST",
      headers: auth(),
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) {
      toast.error(b.detail || "Restore failed");
      return;
    }
    toast.success(`${slug} restored and reactivated`);
    loadTenants();
  };

  // Archive and purge both demand the slug typed back — these are the two
  // actions that take a customer's data away.
  const runDangerAction = async () => {
    const { slug, action } = danger;
    setDangerBusy(true);
    try {
      const isSuspend = action === "suspend";
      const r = await fetch(
        isSuspend
          ? `${API}/platform/tenants/${slug}/active`
          : `${API}/platform/tenants/${slug}/${action}`,
        {
          method: isSuspend ? "PATCH" : "POST",
          headers: { ...auth(), "Content-Type": "application/json" },
          body: JSON.stringify(
            isSuspend ? { active: false } : { confirm_slug: confirmText.trim() }
          ),
        }
      );
      const b = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(b.detail || `${action} failed`);
        return;
      }
      toast.success(
        action === "archive"
          ? `${slug} archived — ${b.documents ?? 0} documents set aside, restorable`
          : action === "suspend"
          ? `${slug} suspended`
          : `${slug} permanently purged`
      );
      setDanger(null);
      setConfirmText("");
      loadTenants();
    } catch {
      toast.error("Network error");
    } finally {
      setDangerBusy(false);
    }
  };

  const resetPassword = async () => {    const r = await fetch(
      `${API}/platform/tenants/${resetting}/reset-admin-password`,
      {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ admin_email: resetEmail, new_password: newPw }),
      }
    );
    const b = await r.json().catch(() => ({}));
    if (!r.ok) {
      toast.error(b.detail || "Reset failed");
      return;
    }
    toast.success("Password reset");
    setResetting(null);
    setNewPw("");
    setResetEmail("");
  };

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-950 px-4"
        data-testid="platform-login"
      >
        <form
          onSubmit={doLogin}
          className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4 text-slate-100"
        >
          <h1 className="text-2xl font-bold">Platform admin</h1>
          <p className="text-sm text-slate-400">
            Sign in with your super-admin credentials.
          </p>
          <div>
            <Label>Email</Label>
            <Input
              data-testid="platform-email"
              type="email"
              required
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="platform-password"
              type="password"
              required
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            />
          </div>
          <Button
            type="submit"
            data-testid="platform-login-btn"
            className="w-full"
          >
            Sign in
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 px-6 py-8"
      data-testid="platform-dashboard"
    >
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tenants</h1>
            <p className="text-slate-400 text-sm">
              Every tow company running on this deployment.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              localStorage.removeItem("platform_token");
              setToken("");
            }}
            data-testid="platform-logout-btn"
          >
            Sign out
          </Button>
        </div>

        {loading && <p className="text-slate-400">Loading…</p>}

        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-left">
              <tr>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Display name</th>
                <th className="px-4 py-2">Users</th>
                <th className="px-4 py-2">Jobs</th>
                <th className="px-4 py-2">Trucks</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr
                  key={t.slug}
                  data-testid={`tenant-row-${t.slug}`}
                  className="border-t border-slate-800"
                >
                  <td className="px-4 py-3 font-mono text-sky-300">{t.slug}</td>
                  <td className="px-4 py-3">{t.display_name}</td>
                  <td className="px-4 py-3">{t.stats?.users ?? 0}</td>
                  <td className="px-4 py-3">{t.stats?.jobs ?? 0}</td>
                  <td className="px-4 py-3">{t.stats?.trucks ?? 0}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        t.archived_at
                          ? "text-amber-400"
                          : t.active !== false
                          ? "text-emerald-400"
                          : "text-slate-500"
                      }
                    >
                      {t.archived_at
                        ? "Archived"
                        : t.protected
                        ? "Yes · protected"
                        : t.active !== false
                        ? "Yes"
                        : "Suspended"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`impersonate-${t.slug}-btn`}
                      onClick={() => impersonate(t.slug)}
                      disabled={!!t.archived_at}
                    >
                      Impersonate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`reset-pw-${t.slug}-btn`}
                      onClick={() => {
                        setResetting(t.slug);
                        setResetEmail(t.admin_email || "");
                      }}
                    >
                      Reset PW
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`export-${t.slug}-btn`}
                      disabled={exporting === t.slug}
                      onClick={() => exportTenant(t.slug)}
                    >
                      {exporting === t.slug ? "Exporting…" : "Export"}
                    </Button>
                    {t.protected && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`unprotect-${t.slug}-btn`}
                        onClick={() => setProtection(t.slug, false)}
                      >
                        Unprotect
                      </Button>
                    )}
                    {!t.protected && !t.archived_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`protect-${t.slug}-btn`}
                        onClick={() => setProtection(t.slug, true)}
                      >
                        Protect
                      </Button>
                    )}
                    {!t.protected && !t.archived_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`toggle-active-${t.slug}-btn`}
                        onClick={() => toggleActive(t.slug, t.active !== false)}
                      >
                        {t.active !== false ? "Suspend" : "Reactivate"}
                      </Button>
                    )}
                    {!t.protected && !t.archived_at && !isEmptyShell(t) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        data-testid={`archive-${t.slug}-btn`}
                        onClick={() => setDanger({ slug: t.slug, action: "archive" })}
                      >
                        Archive
                      </Button>
                    )}
                    {!t.protected && !t.archived_at && isEmptyShell(t) && (
                      // Nothing to archive — a failed signup leaves a row with
                      // no database and no users, so offer the direct delete.
                      <Button
                        size="sm"
                        variant="destructive"
                        data-testid={`purge-${t.slug}-btn`}
                        onClick={() =>
                          setDanger({ slug: t.slug, action: "purge", empty: true })
                        }
                      >
                        Delete
                      </Button>
                    )}
                    {t.archived_at && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`restore-${t.slug}-btn`}
                          onClick={() => restoreTenant(t.slug)}
                        >
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid={`purge-${t.slug}-btn`}
                          onClick={() => setDanger({ slug: t.slug, action: "purge" })}
                        >
                          Purge
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <StorageUsageCard authHeaders={auth} />
        <ExternalMigrationCard authHeaders={auth} />
        <PhotoBackfillCard authHeaders={auth} />
      </div>

      <Dialog
        open={!!danger}
        onOpenChange={(o) => {
          if (!o) {
            setDanger(null);
            setConfirmText("");
          }
        }}
      >
        <DialogContent className="bg-slate-900 text-slate-100 border-slate-800">
          <DialogHeader>
            <DialogTitle>
              {danger?.action === "purge"
                ? danger?.empty
                  ? "Delete leftover row"
                  : "Permanently purge"
                : danger?.action === "suspend"
                ? "Suspend"
                : "Archive"}{" "}
              — {danger?.slug}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {danger?.action === "purge" ? (
              danger?.empty ? (
                <p className="text-slate-300" data-testid="purge-empty-warning">
                  This company has no database, no jobs and no staff — a signup
                  that never finished. There is nothing to archive and nothing
                  to lose; this just removes the leftover row.
                </p>
              ) : (
                <p className="text-rose-300" data-testid="purge-warning">
                  This deletes their archived data for good. There is no undo and
                  no backup on our side. Export it first if there is any chance
                  they will ask for it.
                </p>
              )
            ) : danger?.action === "suspend" ? (
              <p className="text-slate-300" data-testid="suspend-warning">
                Their staff are signed out within seconds and can't log back in
                until you reactivate them. Nothing is deleted, and you can
                reactivate at any time.
              </p>
            ) : (
              <p className="text-slate-300" data-testid="archive-warning">
                Their database is moved aside and their staff lose access
                immediately. Nothing is deleted — you can restore them, or purge
                them later once you are certain.
              </p>
            )}
            {danger?.action !== "suspend" && (
              <div>
                <Label>
                  Type{" "}
                  <span className="font-mono text-sky-300">{danger?.slug}</span>{" "}
                  to confirm
                </Label>
                <Input
                  data-testid="danger-confirm-input"
                  value={confirmText}
                  autoComplete="off"
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDanger(null);
                setConfirmText("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="danger-confirm-btn"
              disabled={
                dangerBusy ||
                (danger?.action !== "suspend" && confirmText.trim() !== danger?.slug)
              }
              onClick={runDangerAction}
            >
              {dangerBusy
                ? "Working…"
                : danger?.action === "purge"
                ? danger?.empty
                  ? "Delete row"
                  : "Purge forever"
                : danger?.action === "suspend"
                ? "Suspend"
                : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>        <DialogContent className="bg-slate-900 text-slate-100 border-slate-800">
          <DialogHeader>
            <DialogTitle>Reset admin password — {resetting}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Admin email</Label>
              <Input
                data-testid="reset-pw-email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>
            <div>
              <Label>New password (min 8)</Label>
              <Input
                data-testid="reset-pw-new"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>
              Cancel
            </Button>
            <Button
              data-testid="reset-pw-confirm-btn"
              disabled={newPw.length < 8}
              onClick={resetPassword}
            >
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
